import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, PencilIcon, PlayIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import {
  getMonthlyTemplates,
  createMonthlyTemplate,
  updateMonthlyTemplate,
  deleteMonthlyTemplate,
  bulkCreatePondSchedule,
  generateTasksFromTemplate,
  triggerSystemTaskGeneration
} from '../utils/api';
import { MonthlyTaskTemplate, MonthlyTaskTemplateCreate, TaskPriority, TaskCategory } from '../types/api';
import { getErrorMessage } from '../utils/api';
import apiClient from '../utils/api';

const TaskTemplatesPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<MonthlyTaskTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MonthlyTaskTemplate | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [creatingBulk, setCreatingBulk] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [generatingSystem, setGeneratingSystem] = useState(false);
  const [systemGenerationResult, setSystemGenerationResult] = useState<any>(null);
  const [deletingDuplicates, setDeletingDuplicates] = useState(false);
  const [sortField, setSortField] = useState<'day_of_month' | 'task_title' | 'category' | 'priority' | 'start_month' | 'is_active'>('day_of_month');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [formData, setFormData] = useState({
    task_title: '',
    task_description: '',
    category: 'general' as TaskCategory,
    priority: 'medium' as TaskPriority,
    day_of_month: 1,
    assigned_to_manager_id: '',
    is_active: true,
    start_month: '',
    end_month: '',
    is_pond_schedule: false,
  });

  const [managers, setManagers] = useState<Array<{ id: string; full_name: string }>>([]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/tasks');
      return;
    }

    if (selectedLocation?.id) {
      loadTemplates();
      loadManagers();
    }
  }, [selectedLocation?.id]);

  const loadManagers = async () => {
    try {
      const response = await apiClient.get('/users');
      const allUsers = response.data || [];
      const managerUsers = allUsers.filter((u: any) => u.role === 'manager' && u.is_active);
      setManagers(managerUsers);
    } catch (err: any) {
      console.error('Error loading managers:', err);
    }
  };

  const loadTemplates = async () => {
    if (!selectedLocation?.id) return;
    try {
      setLoading(true);
      setError('');
      const data = await getMonthlyTemplates(selectedLocation.id);
      setTemplates(data || []);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  };

  // Sort templates
  const sortedTemplates = useMemo(() => {
    const sorted = [...templates];
    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'day_of_month':
          aValue = a.day_of_month || 0;
          bValue = b.day_of_month || 0;
          break;
        case 'task_title':
          aValue = (a.task_title || '').toLowerCase();
          bValue = (b.task_title || '').toLowerCase();
          break;
        case 'category':
          aValue = (a.category || '').toLowerCase();
          bValue = (b.category || '').toLowerCase();
          break;
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
          bValue = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
          break;
        case 'start_month':
          aValue = a.start_month ? new Date(a.start_month).getTime() : 0;
          bValue = b.start_month ? new Date(b.start_month).getTime() : 0;
          break;
        case 'is_active':
          aValue = a.is_active ? 1 : 0;
          bValue = b.is_active ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [templates, sortField, sortDirection]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) {
      return (
        <span className="inline-block w-4 h-4 opacity-30">
          <ChevronUpIcon className="h-3 w-3 -mb-1" />
          <ChevronDownIcon className="h-3 w-3 -mt-1" />
        </span>
      );
    }
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4" />
    ) : (
      <ChevronDownIcon className="h-4 w-4" />
    );
  };

  const handleBulkCreatePondSchedule = async () => {
    if (!selectedLocation?.id) return;
    if (!window.confirm('This will create 9 default pond schedule templates for March-November. Duplicates will be skipped. Continue?')) {
      return;
    }

    try {
      setCreatingBulk(true);
      setError('');
      const result = await bulkCreatePondSchedule(selectedLocation.id);
      await loadTemplates();
      // Show success message with info about skipped duplicates
      if (result.skipped_count !== undefined && result.skipped_count > 0) {
        alert(`${result.message}\n\nNote: ${result.skipped_count} template(s) were skipped because they already exist.`);
      } else {
        alert(result.message);
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to create pond schedule templates'));
    } finally {
      setCreatingBulk(false);
    }
  };

  const handleTriggerSystemGeneration = async () => {
    if (!window.confirm('This will generate all system tasks (vaccination, weight, deworming, kidding, missing logs, harvest, monthly pond schedule). Continue?')) {
      return;
    }

    try {
      setGeneratingSystem(true);
      setError('');
      setSystemGenerationResult(null);
      const result = await triggerSystemTaskGeneration();
      setSystemGenerationResult(result);
      // Reload tasks page would be ideal, but we're on templates page
      // User can navigate to tasks page to see generated tasks
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to generate system tasks'));
    } finally {
      setGeneratingSystem(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.task_title.trim()) {
      setError('Task title is required');
      return;
    }

    if (!selectedLocation?.id) {
      setError('Location is required');
      return;
    }

    try {
      setError('');
      const templateData: MonthlyTaskTemplateCreate = {
        location_id: selectedLocation.id,
        task_title: formData.task_title,
        task_description: formData.task_description || undefined,
        category: formData.category,
        priority: formData.priority,
        day_of_month: formData.day_of_month,
        assigned_to_manager_id: formData.assigned_to_manager_id || undefined,
        is_active: formData.is_active,
        start_month: formData.start_month || undefined,
        end_month: formData.end_month || undefined,
        is_pond_schedule: formData.is_pond_schedule,
      };

      if (editingTemplate) {
        await updateMonthlyTemplate(editingTemplate.id, templateData);
      } else {
        await createMonthlyTemplate(templateData);
      }

      setShowForm(false);
      setEditingTemplate(null);
      resetForm();
      await loadTemplates();
    } catch (err: any) {
      setError(getErrorMessage(err, editingTemplate ? 'Failed to update template' : 'Failed to create template'));
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setDeletingId(templateId);
      await deleteMonthlyTemplate(templateId);
      await loadTemplates();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to delete template'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteDuplicates = async () => {
    if (!selectedLocation?.id) return;

    // Find duplicates based on: task_title, day_of_month, location_id, is_pond_schedule
    const duplicateGroups = new Map<string, MonthlyTaskTemplate[]>();
    
    templates.forEach(template => {
      const key = `${template.task_title}|${template.day_of_month}|${template.is_pond_schedule}`;
      if (!duplicateGroups.has(key)) {
        duplicateGroups.set(key, []);
      }
      duplicateGroups.get(key)!.push(template);
    });

    // Filter to only groups with more than one template
    const actualDuplicates = Array.from(duplicateGroups.values())
      .filter(group => group.length > 1);

    if (actualDuplicates.length === 0) {
      alert('No duplicate templates found.');
      return;
    }

    // Count total duplicates (keeping first one of each group)
    const totalDuplicates = actualDuplicates.reduce((sum, group) => sum + group.length - 1, 0);
    const duplicateIds: string[] = [];
    
    actualDuplicates.forEach(group => {
      // Keep the first one (or the one with the earliest created_at if available)
      // Sort by id to ensure consistent ordering
      const sorted = [...group].sort((a, b) => a.id.localeCompare(b.id));
      // Delete all except the first
      sorted.slice(1).forEach(template => duplicateIds.push(template.id));
    });

    if (!window.confirm(
      `Found ${actualDuplicates.length} duplicate group(s) with ${totalDuplicates} duplicate template(s).\n\n` +
      `This will delete ${totalDuplicates} duplicate template(s), keeping 1 of each group.\n\n` +
      `Continue?`
    )) {
      return;
    }

    try {
      setDeletingDuplicates(true);
      setError('');
      
      // Delete all duplicates
      let deletedCount = 0;
      let failedCount = 0;
      
      for (const templateId of duplicateIds) {
        try {
          await deleteMonthlyTemplate(templateId);
          deletedCount++;
        } catch (err: any) {
          console.error(`Failed to delete template ${templateId}:`, err);
          failedCount++;
        }
      }

      await loadTemplates();
      
      if (failedCount > 0) {
        alert(`Deleted ${deletedCount} duplicate(s). ${failedCount} failed to delete.`);
      } else {
        alert(`Successfully deleted ${deletedCount} duplicate template(s).`);
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to delete duplicates'));
    } finally {
      setDeletingDuplicates(false);
    }
  };

  const handleGenerate = async (templateId: string) => {
    if (!window.confirm('Generate a task from this template now?')) {
      return;
    }

    try {
      setGeneratingId(templateId);
      setError('');
      const result = await generateTasksFromTemplate(templateId);
      if (result.task_id) {
        alert('Task generated successfully!');
      } else {
        alert('Task already exists for this month.');
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to generate task from template'));
    } finally {
      setGeneratingId(null);
    }
  };

  const handleEdit = (template: MonthlyTaskTemplate) => {
    setEditingTemplate(template);
    setFormData({
      task_title: template.task_title,
      task_description: template.task_description || '',
      category: template.category,
      priority: template.priority,
      day_of_month: template.day_of_month,
      assigned_to_manager_id: template.assigned_to_manager_id || '',
      is_active: template.is_active,
      start_month: template.start_month || '',
      end_month: template.end_month || '',
      is_pond_schedule: template.is_pond_schedule,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      task_title: '',
      task_description: '',
      category: 'general',
      priority: 'medium',
      day_of_month: 1,
      assigned_to_manager_id: '',
      is_active: true,
      start_month: '',
      end_month: '',
      is_pond_schedule: false,
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (user?.role !== 'admin') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Admin access required</p>
        <Link to="/tasks" className="text-blue-600 hover:text-blue-800">Back to Tasks</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Monthly Task Templates</h1>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleTriggerSystemGeneration}
            disabled={generatingSystem}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm sm:text-base"
            title="Generate all system tasks (vaccination, weight, deworming, kidding, missing logs, harvest, monthly pond schedule)"
          >
            <PlayIcon className="h-5 w-5" />
            <span className="whitespace-nowrap">{generatingSystem ? 'Generating...' : 'Generate System Tasks'}</span>
          </button>
          <button
            onClick={handleBulkCreatePondSchedule}
            disabled={creatingBulk}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <PlayIcon className="h-5 w-5" />
            {creatingBulk ? 'Creating...' : 'Bulk Create Pond Schedule'}
          </button>
          <button
            onClick={handleDeleteDuplicates}
            disabled={deletingDuplicates || templates.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            title="Delete duplicate templates (same title, day, and pond schedule flag)"
          >
            <TrashIcon className="h-5 w-5" />
            {deletingDuplicates ? 'Deleting...' : 'Delete Duplicates'}
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingTemplate(null);
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Create Template
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* System Generation Result */}
      {systemGenerationResult && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-900 mb-2">System Task Generation Complete!</h3>
          <div className="text-sm text-green-800 space-y-1">
            <p><strong>Total Tasks Created:</strong> {systemGenerationResult.results?.total || 0}</p>
            {systemGenerationResult.results && (
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                {systemGenerationResult.results.vaccination > 0 && (
                  <p>• Vaccination: {systemGenerationResult.results.vaccination}</p>
                )}
                {systemGenerationResult.results.weight > 0 && (
                  <p>• Weight: {systemGenerationResult.results.weight}</p>
                )}
                {systemGenerationResult.results.deworming > 0 && (
                  <p>• Deworming: {systemGenerationResult.results.deworming}</p>
                )}
                {systemGenerationResult.results.kidding > 0 && (
                  <p>• Kidding: {systemGenerationResult.results.kidding}</p>
                )}
                {systemGenerationResult.results.missing_logs > 0 && (
                  <p>• Missing Logs: {systemGenerationResult.results.missing_logs}</p>
                )}
                {systemGenerationResult.results.harvest > 0 && (
                  <p>• Harvest: {systemGenerationResult.results.harvest}</p>
                )}
                {systemGenerationResult.results.monthly_pond > 0 && (
                  <p>• Monthly Pond: {systemGenerationResult.results.monthly_pond}</p>
                )}
                {systemGenerationResult.results.overdue_notifications > 0 && (
                  <p>• Overdue Notifications: {systemGenerationResult.results.overdue_notifications}</p>
                )}
              </div>
            )}
          </div>
          <Link
            to="/tasks"
            className="mt-3 inline-block text-sm text-green-700 hover:text-green-900 font-medium underline"
          >
            View Generated Tasks →
          </Link>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">
            {editingTemplate ? 'Edit Template' : 'Create Template'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.task_title}
                onChange={(e) => setFormData({ ...formData, task_title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.task_description}
                onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as TaskCategory })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="general">General</option>
                  <option value="health">Health</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="data_entry">Data Entry</option>
                  <option value="breeding">Breeding</option>
                  <option value="harvest">Harvest</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Day of Month <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max="31"
                value={formData.day_of_month}
                onChange={(e) => setFormData({ ...formData, day_of_month: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Day of month when task should be generated (1-31)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To Manager</label>
              <select
                value={formData.assigned_to_manager_id}
                onChange={(e) => setFormData({ ...formData, assigned_to_manager_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Auto-assign (first manager)</option>
                {managers.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.full_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Month</label>
                <input
                  type="date"
                  value={formData.start_month}
                  onChange={(e) => setFormData({ ...formData, start_month: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Month</label>
                <input
                  type="date"
                  value={formData.end_month}
                  onChange={(e) => setFormData({ ...formData, end_month: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_pond_schedule"
                checked={formData.is_pond_schedule}
                onChange={(e) => setFormData({ ...formData, is_pond_schedule: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_pond_schedule" className="text-sm font-medium text-gray-700">
                Pond Schedule Template
              </label>
            </div>

            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingTemplate ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingTemplate(null);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Templates List */}
      {loading ? (
        <div className="p-8 text-center">
          <LoadingSpinner size="md" />
          <p className="text-gray-500 mt-2">Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-600 mb-4">No templates found</p>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            Create your first template
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('task_title')}
                  >
                    <div className="flex items-center gap-1">
                      Title
                      <SortIcon field="task_title" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('day_of_month')}
                  >
                    <div className="flex items-center gap-1">
                      Day
                      <SortIcon field="day_of_month" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center gap-1">
                      Category
                      <SortIcon field="category" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('priority')}
                  >
                    <div className="flex items-center gap-1">
                      Priority
                      <SortIcon field="priority" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('start_month')}
                  >
                    <div className="flex items-center gap-1">
                      Period
                      <SortIcon field="start_month" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('is_active')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      <SortIcon field="is_active" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedTemplates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{template.task_title}</div>
                      {template.is_pond_schedule && (
                        <span className="text-xs text-purple-600">Pond Schedule</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{template.day_of_month}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{template.category.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        template.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                        template.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        template.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {template.priority.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(template.start_month)} - {formatDate(template.end_month)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleGenerate(template.id)}
                          disabled={generatingId === template.id || !template.is_active}
                          className="text-green-600 hover:text-green-800 disabled:opacity-50"
                          title="Generate task from template"
                        >
                          <PlayIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(template)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit template"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          disabled={deletingId === template.id}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Delete template"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskTemplatesPage;

